import ApiCall from 'apps/nhsv/apiCalls/nhsv-webhooks';
import { DynamicFilterForm } from 'common/dynamic-filter-form';
import { DetailPage, ListPage } from 'common/ui/admin-CRUD/page';
import { useMutation } from '@ngtrvu/use-api';
import { useQueryClient } from '@tanstack/react-query';
import { Form, message } from 'antd';
import PageTitle from 'apps/home/components/PageTitle';
import MetaDecorator from 'apps/home/components/MetaDecorator';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetchApiGet } from 'hooks/api';
import { useEffect } from 'react';
import { columnsGenerator } from './config';
import ItemDetail from './detail';
import ItemForm from './form';

export const NHSVWebhookListPage = () => (
  <ListPage
    title="NHSV Webhook Configurations"
    apiCall={ApiCall}
    columnsGenerator={columnsGenerator}
    FilterForm={DynamicFilterForm}
    createUrl="/nhsv/webhook-configs/create"
  />
);

export const NHSVWebhookDetailPage = () => (
  <DetailPage
    title="NHSV Webhook Configuration"
    apiCall={ApiCall}
    listUrl="/nhsv/webhook-configs"
    ItemDetail={ItemDetail}
    options={{ editEnabled: true }}
  />
);

// Custom edit page: flattens retry_policy from the fetched item for form fields,
// then re-nests them before sending PATCH to the backend.
export const NHSVWebhookEditPage = () => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: item, isLoading, load } = useFetchApiGet(ApiCall.detail, {
    resourceName: 'data',
  });

  useEffect(() => {
    if (id) load({ id });
  }, [id]);

  const initialValues = item
    ? {
        target_url: item.target_url,
        timeout_ms: item.timeout_ms,
        retry_policy_max_bursts: item.retry_policy?.max_bursts,
        retry_policy_attempts_per_burst: item.retry_policy?.attempts_per_burst,
        retry_policy_cooldown_minutes: item.retry_policy?.cooldown_minutes,
      }
    : {};

  // @ts-ignore — local ApiCall shape differs from @ngtrvu/use-api's ApiCall type
  const { mutate: updateConfig, isPending } = useMutation(ApiCall.edit, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ApiCall.detail.queryKey] });
      queryClient.invalidateQueries({ queryKey: [ApiCall.list.queryKey] });
      message.success('Webhook configuration updated successfully');
      navigate('/nhsv/webhook-configs');
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to update webhook configuration');
    },
  });

  const onFinish = (values: any) => {
    const {
      target_url,
      timeout_ms,
      retry_policy_max_bursts,
      retry_policy_attempts_per_burst,
      retry_policy_cooldown_minutes,
    } = values;

    updateConfig({
      id,
      payload: {
        target_url,
        timeout_ms,
        retry_policy: {
          max_bursts: retry_policy_max_bursts,
          attempts_per_burst: retry_policy_attempts_per_burst,
          cooldown_minutes: retry_policy_cooldown_minutes,
        },
      },
    });
  };

  if (isLoading) return null;

  return (
    <>
      <MetaDecorator title="Edit NHSV Webhook Configuration" />
      <PageTitle
        title="Edit NHSV Webhook Configuration"
        backUrl="/nhsv/webhook-configs"
      />
      <ItemForm
        form={form}
        submiting={isPending}
        onFinish={onFinish}
        initialValues={initialValues}
        name="nhsv-webhook-edit"
        isCreate={false}
      />
    </>
  );
};

// Custom create page: extracts event_id from form values and calls
// POST /nhsv/webhook/events/{eventID}/configs with the rest as body.
export const NHSVWebhookCreatePage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // @ts-ignore — local ApiCall shape differs from @ngtrvu/use-api's ApiCall type
  const { mutate: createConfig, isPending } = useMutation(ApiCall.create, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ApiCall.list.queryKey] });
      message.success('Webhook configuration created successfully');
      navigate('/nhsv/webhook-configs');
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to create webhook configuration');
    },
  });

  const onFinish = (values: any) => {
    const {
      event_id,
      target_url,
      timeout_ms,
      retry_policy_max_bursts,
      retry_policy_attempts_per_burst,
      retry_policy_cooldown_minutes,
    } = values;

    createConfig({
      eventID: String(event_id),
      payload: {
        target_url,
        timeout_ms,
        retry_policy: {
          max_bursts: retry_policy_max_bursts,
          attempts_per_burst: retry_policy_attempts_per_burst,
          cooldown_minutes: retry_policy_cooldown_minutes,
        },
      },
    });
  };

  return (
    <>
      <MetaDecorator title="Create NHSV Webhook Configuration" />
      <PageTitle
        title="Create NHSV Webhook Configuration"
        backUrl="/nhsv/webhook-configs"
      />
      <ItemForm
        form={form}
        submiting={isPending}
        onFinish={onFinish}
        initialValues={{}}
        name="nhsv-webhook-create"
        isCreate
      />
    </>
  );
};
